/**
 * The participant's side of the wire, shared by every participant-facing page.
 *
 * Participants never hold a JWT. Two credentials exist: the SESSION token
 * (sessionStorage, one class, dies with the tab) and, since M25, the PERSONAL
 * key (localStorage, one person, outlives the tab — it is what lets a phone be
 * recognised next month). Every participant fetch sends whichever it has.
 */
import { ApiError } from './api.js';

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/+$/, '');

/** Storage key for the session token. Per-tab and cleared when the tab closes. */
export const TOKEN_KEY = 'attest.checkin.token';

/**
 * Storage key for the PERSONAL key (M25). localStorage, deliberately: this is
 * the thing that must survive the tab closing, the phone sleeping and next
 * month's scan. Every access is wrapped — a private window or a browser set
 * to block site data throws on the accessor, and that must read as "no key",
 * never as a broken page.
 */
export const PERSONAL_KEY = 'attest.participant.key';

export function readPersonalKey() {
  try {
    return localStorage.getItem(PERSONAL_KEY);
  } catch {
    return null;
  }
}
export function storePersonalKey(key) {
  try {
    localStorage.setItem(PERSONAL_KEY, key);
  } catch {
    /* the phone will get the form next time; nothing else is lost */
  }
}
export function clearPersonalKey() {
  try {
    localStorage.removeItem(PERSONAL_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** A fetch that speaks participant, not staff — x-session-token, never a JWT. */
export async function participantFetch(path, { token, ...options } = {}) {
  const headers = new Headers(options.headers ?? {});
  if (token) headers.set('x-session-token', token);
  const personal = readPersonalKey();
  if (personal) headers.set('x-participant-key', personal);
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  let res;
  try {
    res = await fetch(`${baseUrl}${path}`, { ...options, headers });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No connection. Check your signal and try again.');
  }

  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'REQUEST_FAILED',
      body?.error?.message ?? `Something went wrong (${res.status}).`
    );
  }
  return body?.data ?? body;
}
